import React from 'react';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  CogIcon
} from '@heroicons/react/20/solid';

const jpmorganFeatures = [
  {
    name: 'Institutional Quality',
    icon: AcademicCapIcon,
    description: 'JPMorgan Asset Management brings institutional-grade investment strategies and research capabilities to the advisor channel.',
    details: [
      "Institutional investment expertise",
      "Global research capabilities",
      "Risk management focus",
      "Professional portfolio management"
    ]
  },
  {
    name: 'Global Research',
    icon: GlobeAltIcon,
    description: 'Leverages JPMorgan\'s global research network and economic insights to inform investment decisions across markets.',
    details: [
      "Global market coverage",
      "Economic research insights",
      "Cross-regional expertise",
      "Macro-driven strategies"
    ]
  },
  {
    name: 'Diversified Offerings',
    icon: CogIcon,
    description: 'Offers a comprehensive suite of investment solutions across asset classes and investment styles.',
    details: [
      "Multi-asset strategies",
      "Various investment styles",
      "Risk-targeted solutions",
      "Customizable allocations"
    ]
  }
];

const comparisonData = [
  {
    feature: 'Investment Philosophy',
    jpmorgan: 'Institutional, diversified approach',
    sharpemetrix: 'Systematic, alpha-seeking'
  },
  {
    feature: 'Minimum Investment',
    jpmorgan: 'High minimums for institutional quality',
    sharpemetrix: 'Accessible to all advisor sizes'
  },
  {
    feature: 'Strategy Complexity',
    jpmorgan: 'Complex institutional products',
    sharpemetrix: 'Transparent, rules-based'
  },
  {
    feature: 'Data Sources',
    jpmorgan: 'Traditional research + macro insights',
    sharpemetrix: 'Alternative data signals'
  },
  {
    feature: 'Customization',
    jpmorgan: 'Limited to product selection',
    sharpemetrix: 'High flexibility'
  },
  {
    feature: 'Fee Structure',
    jpmorgan: 'Institutional pricing tiers',
    sharpemetrix: 'Lower fees through automation'
  },
  {
    feature: 'Transparency',
    jpmorgan: 'Limited strategy disclosure',
    sharpemetrix: 'Full process transparency'
  }
];

export default function JPMorganAssetManagementPage() {
  return (
    <>
      <SEO 
        title="J.P. Morgan Asset Management vs SharpeMetrix: TAMP Comparison"
        description="Compare J.P. Morgan Asset Management institutional strategies with SharpeMetrix systematic approach. Understand differences in accessibility, fees, and methodology."
        keywords="JPMorgan Asset Management, J.P. Morgan Asset Management, JPMorgan vs SharpeMetrix, TAMP comparison, institutional strategies, asset management, investment solutions"
        url="/jpmorgan-asset-management"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background Gradient */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#305CFE] to-[#02D3FF] opacity-20"
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-3xl pt-24 text-center sm:pt-40 pb-16 sm:pb-24">
              <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
                J.P. Morgan Asset Management vs SharpeMetrix
              </h1>
              <p className="mt-6 text-xl leading-8 text-text-200">
                Compare J.P. Morgan Asset Management's institutional-grade strategies with SharpeMetrix's accessible, systematic approach to understand which solution best serves your practice.
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">

            {/* JPMorgan Overview Section */}
            <section className="mb-16 lg:mb-24">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Understanding J.P. Morgan Asset Management
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  J.P. Morgan Asset Management offers institutional-grade investment strategies for advisors, leveraging the firm's global research capabilities and diversified investment expertise.
                </p>
              </div>
            </section>

            {/* JPMorgan Features Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                JPMorgan's Investment Approach
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                {jpmorganFeatures.map((feature) => (
                  <div key={feature.name} className="flex flex-col items-start">
                    <div className="flex items-center gap-x-3 mb-4">
                      <feature.icon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                      <h3 className="text-xl font-semibold leading-7 text-text-100">{feature.name}</h3>
                    </div>
                    <p className="text-base leading-7 text-text-200 mb-3">{feature.description}</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text-300 pl-2">
                      {feature.details.map((detail, index) => <li key={index}>{detail}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Comparison Section */}
            <section className="mb-16 lg:mb-24">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                JPMorgan Asset Management vs SharpeMetrix: Detailed Comparison
              </h2>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg bg-bg-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-bg-300">
                    <thead className="bg-bg-100">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-text-100">Feature</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">JPMorgan Asset Management</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">SharpeMetrix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300 bg-bg-200">
                      {comparisonData.map((item, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-text-100">{item.feature}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.jpmorgan}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.sharpemetrix}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* SharpeMetrix Advantages Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                SharpeMetrix Advantages
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <CurrencyDollarIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Accessible to All Advisors</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    While JPMorgan requires high minimums for institutional access, SharpeMetrix provides institutional-quality strategies accessible to advisors of all sizes.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <ChartBarIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Simplified Excellence</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    Our systematic approach delivers institutional-quality results without the complexity and opacity of traditional institutional products.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <CogIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Alternative Data Edge</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    While JPMorgan relies on traditional research, SharpeMetrix leverages proprietary alternative data signals for unique investment insights.
                  </p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 ring-1 ring-white/10 shadow-lg">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">
                  Ready for Institutional Quality Without the Barriers?
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  Experience how SharpeMetrix democratizes institutional-grade investment management for advisors of all sizes.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <a
                    href="/register"
                    className="inline-flex items-center gap-x-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                  >
                    Start Free 30-Day Trial
                  </a>
                  <a href="/tamp-comparison" className="text-sm font-semibold leading-6 text-text-200">
                    View All Comparisons <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
} 